<template>

    <!-- Locked Status Warning Banner -->
    <div v-if="isFormLocked" class="alert alert-warning d-flex align-items-center mb-3" role="alert">
        <i class="fas fa-lock me-2"></i>
        <div class="flex-grow-1">
            <strong>This request is locked.</strong> 
            The status is set to "{{ currentStatusName }}" which prevents editing. 
            Change the status to make edits to this request.
        </div>
        <button
            v-if="isSystemAdmin"
            class="btn btn-sm btn-warning ms-3"
            @click="adminUnlocked = true"
            title="Admin override: unlock all fields for editing"
        >
            <LockOpen :size="14" class="me-1" />
            Unlock
        </button>
    </div>
    
    <!-- Action Toolbar (only shown when job has an Id) -->
    <div v-if="model.Id" class="d-flex gap-2 mb-3 p-2 bg-light border rounded">
        <button 
            class="btn btn-sm btn-outline-primary"
            @click="startClock"
            :disabled="isFormLocked"
            title="Start Clock"
        >
            <PlayCircle :size="16" class="me-1" />
            Start Clock
        </button>
        <button 
            class="btn btn-sm btn-outline-secondary"
            @click="addAssignment"
            :disabled="isFormLocked"
            title="Add Task"
        >
            <ClipboardCheck :size="16" class="me-1" />
            Add Task
        </button>
        <button 
            class="btn btn-sm btn-outline-success"
            @click="addWork"
            :disabled="isFormLocked"
            title="Add Work"
        >
            <Clock :size="16" class="me-1" />
            Add Work
        </button>
        <button 
            class="btn btn-sm btn-outline-warning"
            @click="addPart"
            :disabled="isFormLocked"
            title="Add Part"
        >
            <Package :size="16" class="me-1" />
            Add Part
        </button>
        <button 
            class="btn btn-sm btn-outline-secondary"
            @click="addExpense"
            :disabled="isFormLocked"
            title="Add Expense"
        >
            <Banknote :size="16" class="me-1" />
            Add Expense
        </button>
        <button 
            class="btn btn-sm btn-outline-secondary"
            @click="addMileage"
            :disabled="isFormLocked"
            title="Add Mileage"
        >
            <Car :size="16" class="me-1" />
            Add Mileage
        </button>
        <button 
            class="btn btn-sm btn-outline-info"
            @click="addEstimate"
            :disabled="isFormLocked"
            title="Add Estimate"
        >
            <FileText :size="16" class="me-1" />
            Add Estimate
        </button>
        <button 
            class="btn btn-sm btn-outline-success"
            @click="openNewInvoice"
            :disabled="isFormLocked"
            title="Create Invoice"
        >
            <FileStack :size="16" class="me-1" />
            Create Invoice
        </button>
        <button 
            class="btn btn-sm btn-outline-secondary ms-auto"
            @click="refreshJob"
            title="Refresh Job"
        >
            <RefreshCw :size="16" class="me-1" />
            Refresh
        </button>
        <button 
            class="btn btn-sm btn-outline-danger"
            @click="deleteJob"
            :disabled="isFormLocked"
            title="Delete Job"
        >
            <Trash2 :size="16" class="me-1" />
            Delete
        </button>
    </div>
    
    <!-- Main Form Tabs -->
    <ul class="nav nav-tabs" role="tablist">
        <li class="nav-item" role="presentation">
            <button 
                class="nav-link" 
                :class="{ active: activeFormTab === 'workorder' }"
                @click="activeFormTab = 'workorder'"
                type="button"
            >
                <span class="badge" :class="activeFormTab === 'workorder' ? 'tab-badge-active' : 'tab-badge'">
                    <ClipboardList :size="16" class="me-1" style="vertical-align: text-bottom;" />
                    Work Order Information
                </span>
            </button>
        </li>
        <li class="nav-item" role="presentation">
            <button 
                class="nav-link" 
                :class="{ active: activeFormTab === 'status' }"
                @click="activeFormTab = 'status'"
                type="button"
            >
                <span class="badge" :class="activeFormTab === 'status' ? 'tab-badge-active' : 'tab-badge'">
                    <CalendarClock :size="16" class="me-1" style="vertical-align: text-bottom;" />
                    Status & Scheduling
                </span>
            </button>
        </li>
        <!-- Commented out per user request - task #8
        <li v-if="hasBillingAccess" class="nav-item" role="presentation">
            <button 
                class="nav-link" 
                :class="{ active: activeFormTab === 'billing' }"
                @click="activeFormTab = 'billing'"
                type="button"
            >
                <span class="badge" :class="activeFormTab === 'billing' ? 'tab-badge-active' : 'tab-badge'">
                    <DollarSign :size="16" class="me-1" style="vertical-align: text-bottom;" />
                    Project and Billing Info
                </span>
            </button>
        </li>
        -->
        <li v-if="isSystemAdmin" class="nav-item" role="presentation">
            <button 
                class="nav-link" 
                :class="{ active: activeFormTab === 'access' }"
                @click="activeFormTab = 'access'"
                type="button"
            >
                <span class="badge" :class="activeFormTab === 'access' ? 'tab-badge-active' : 'tab-badge'">
                    <ShieldCheck :size="16" class="me-1" style="vertical-align: text-bottom;" />
                    Access Control
                </span>
            </button>
        </li>
        <li v-if="model.Id" class="nav-item" role="presentation">
            <button 
                class="nav-link" 
                :class="{ active: activeFormTab === 'messages' }"
                @click="activeFormTab = 'messages'"
                type="button"
            >
                <span class="badge" :class="activeFormTab === 'messages' ? 'tab-badge-active' : 'tab-badge'">
                    <MessageSquare :size="16" class="me-1" style="vertical-align: text-bottom;" />
                    Messages
                </span>
            </button>
        </li>
        <li v-if="model.Id" class="nav-item" role="presentation">
            <button 
                class="nav-link" 
                :class="{ active: activeFormTab === 'notes' }"
                @click="activeFormTab = 'notes'"
                type="button"
            >
                <span class="badge" :class="activeFormTab === 'notes' ? 'tab-badge-active' : 'tab-badge'">
                    <FileText :size="16" class="me-1" style="vertical-align: text-bottom;" />
                    Notes
                </span>
            </button>
        </li>
        <!-- Summary badges (right-aligned) -->
        <li v-if="model.Id" class="ms-auto d-flex align-items-center gap-2 pe-2" style="list-style: none;">
            <span class="badge bg-primary" title="Requests">
                <ClipboardList :size="14" class="me-1" style="vertical-align: text-bottom;" />
                Requests: {{ tabCounts.requests }}
            </span>
            <span class="badge bg-info" title="Estimates">
                <FileText :size="14" class="me-1" style="vertical-align: text-bottom;" />
                Estimates: {{ tabCounts.estimates }}
            </span>
            <span class="badge bg-success" title="Invoices">
                <FileStack :size="14" class="me-1" style="vertical-align: text-bottom;" />
                Invoices: {{ tabCounts.invoices }}
            </span>
        </li>
    </ul>

    <div class="tab-content border border-top-0 p-3 bg-light">
        <!-- Work Order Information Tab -->
        <div v-show="activeFormTab === 'workorder'">
            <div class="row">
                <!-- Column 1: Main Work Order Fields -->
                <div class="col-md-6">
                    <!-- Row 1: Client, Request Description -->
                    <div class="row">
                        <div class="col-6">
                            <label class="d-block">
                                Client
                            </label>
                            <client-ac 
                                v-model="model.Customer" 
                                input-class="form-control form-control-sm" 
                                :disabled="isFormLocked" 
                                :readonly="isFormLocked"
                            ></client-ac>
                        </div>
                        <div class="col-6">
                            <kt-input
                                type="text"
                                label="Work Order Title" 
                                input-class="form-control form-control-sm"
                                v-model="model.RequestDetailedDescription"
                                placeholder="Work Order Title"
                                :disabled="isFormLocked"
                                :readonly="isFormLocked"
                            ></kt-input>
                        </div>
                    </div>
                    
                    <!-- Row 2: Priority, Due Date, Create Date -->
                    <div class="row mt-2">
                        <div class="col-4">
                            <kt-select
                                v-model="model.Priority"
                                :options="priorityOptions"
                                label="Priority"
                                label-key="label"
                                value-key="value"
                                placeholder="Choose Priority"
                                :disabled="isFormLocked"
                            ></kt-select>
                        </div>
                        <div class="col-4">
                            <kt-input
                                type="date"
                                label="Due Date"
                                input-class="form-control form-control-sm"
                                v-model="model.DueDate"
                                :disabled="isFormLocked"
                                :readonly="isFormLocked"
                            ></kt-input>
                        </div>
                        <div class="col-4">
                            <kt-input
                                type="date"
                                label="Create Date"
                                input-class="form-control form-control-sm"
                                v-model="model.CreateDate"
                                :disabled="true"
                                :readonly="true"
                            ></kt-input>
                        </div>
                    </div>
                    
                    <!-- Row 3: Assigned Owner + Department/Category -->
                    <div class="row mt-2">
                        <div class="col-md-6">
                            <tech-select 
                                label="Assigned Owner"
                                :active="1" 
                                v-model="model.Technician"
                                :disabled="isFormLocked"
                            ></tech-select>
                        </div>
                        <div class="col-md-6">
                            <label class="d-block mb-1">Department / Category</label>
                            <div class="input-group input-group-sm">
                                <select 
                                    v-if="departmentOptions.length > 0"
                                    class="form-select form-select-sm"
                                    v-model="model.Department"
                                    :disabled="isFormLocked"
                                >
                                    <option :value="undefined">Select Department...</option>
                                    <option 
                                        v-for="dept in departmentOptions" 
                                        :key="dept.value" 
                                        :value="dept.value"
                                    >
                                        {{ dept.label }}
                                    </option>
                                </select>
                                <select 
                                    v-else
                                    class="form-select form-select-sm"
                                    disabled
                                >
                                    <option>Use button to add department</option>
                                </select>
                                <button 
                                    type="button" 
                                    class="btn btn-sm btn-outline-primary"
                                    @click="openDepartmentSelector"
                                    title="Manage departments"
                                    :disabled="isFormLocked"
                                >
                                    <Search :size="16" />
                                </button>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Row 4: Assigned Technicians (using TechnicianAc with badges) -->
                    <div class="row mt-2">
                        <div class="col-md-6">
                            <label class="d-block mb-1">
                                <Users :size="16" class="me-1" style="vertical-align: text-bottom;" />
                                Assigned Technicians
                            </label>
                            <div class="d-flex align-items-center gap-2 mb-2" style="max-width: 400px;">
                                <div class="flex-grow-1">
                                    <tech-ac 
                                        v-model="selectedTechToAdd"
                                        placeholder="Search technician to add..."
                                        :disabled="isFormLocked"
                                    ></tech-ac>
                                </div>
                                <button 
                                    class="btn btn-sm btn-outline-primary" 
                                    type="button"
                                    @click="addAssignedTechnician"
                                    :disabled="!selectedTechToAdd || isFormLocked"
                                >
                                    <i class="fas fa-plus"></i> Add
                                </button>
                            </div>
                            <div v-if="assignedTechnicians.length === 0" class="text-muted fst-italic mb-2 small">
                                No technicians assigned
                            </div>
                            <div v-else class="mb-2">
                                <span 
                                    v-for="tech in assignedTechnicians" 
                                    :key="tech.id" 
                                    class="badge bg-secondary me-2 mb-1 d-inline-flex align-items-center"
                                    style="font-size: 0.85em;"
                                >
                                    {{ tech.name }}
                                    <button 
                                        type="button" 
                                        class="btn-close btn-close-white ms-2" 
                                        style="font-size: 0.5em;"
                                        @click="removeAssignedTechnician(tech.id)"
                                        :disabled="isFormLocked"
                                    ></button>
                                </span>
                            </div>
                        </div>
                        <!-- Request Type (multi-select like Departments) -->
                        <div class="col-md-6">
                            <label class="d-block mb-1">Request Type</label>
                            <div class="input-group input-group-sm">
                                <select 
                                    v-if="requestTypeOptions.length > 0"
                                    class="form-select form-select-sm"
                                    v-model="model.RequestType"
                                    :disabled="isFormLocked"
                                >
                                    <option :value="undefined">Select Request Type...</option>
                                    <option 
                                        v-for="type in requestTypeOptions" 
                                        :key="type.value" 
                                        :value="type.value"
                                    >
                                        {{ type.label }}
                                    </option>
                                </select>
                                <select 
                                    v-else
                                    class="form-select form-select-sm"
                                    disabled
                                >
                                    <option>Use button to add request types</option>
                                </select>
                                <button 
                                    type="button" 
                                    class="btn btn-sm btn-outline-primary"
                                    @click="showRequestTypeSelector = true"
                                    title="Manage request types"
                                    :disabled="isFormLocked"
                                >
                                    <Search :size="16" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- Column 2: Project Folder and Internal Notes -->
                <div class="col-md-6">
                    <!-- Row 1: Project Folder and Project Checkbox -->
                    <div class="row">
                        <div class="col-9">
                            <label class="d-flex justify-content-between align-items-center mb-1">
                                <span>Project Folder</span>
                                <button 
                                    v-if="model.ProjectFolder"
                                    type="button" 
                                    class="btn btn-sm btn-link p-0"
                                    @click="openProjectFolder"
                                    title="Open Project Folder"
                                >
                                    <ExternalLink :size="16" />
                                </button>
                            </label>
                            <input
                                type="text"
                                class="form-control form-control-sm"
                                v-model="model.ProjectFolder"
                                placeholder="Project Folder Path or URL"
                                :disabled="isFormLocked"
                                :readonly="isFormLocked"
                            />
                        </div>
                        <div class="col-3">
                            <label class="d-block mb-1">&nbsp;</label>
                            <div class="form-check mt-1">
                                <input 
                                    class="form-check-input" 
                                    type="checkbox" 
                                    v-model="model.Project"
                                    id="projectCheckbox"
                                    :disabled="isFormLocked"
                                >
                                <label class="form-check-label" for="projectCheckbox">
                                    Is Project
                                </label>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Row 2: Description (using TaskDetail field) -->
                    <div class="row mt-2">
                        <div class="col-12">
                            <kt-input
                                type="textarea"
                                label="Description" 
                                input-class="form-control form-control-sm h-200px"
                                v-model="model.TaskDetail"
                                placeholder="Description about this job..."
                                :rows="10"
                                :disabled="isFormLocked"
                                :readonly="isFormLocked"
                            ></kt-input>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Billing Notes (full width at bottom of Work Order tab) -->
            <div v-if="hasBillingAccess" class="row mt-3">
                <div class="col-12">
                    <kt-input
                        type="textarea"
                        label="Billing Notes"
                        input-class="form-control form-control-sm"
                        v-model="model.BillingNotes"
                        placeholder="Billing notes..."
                        :rows="3"
                        :disabled="isFormLocked"
                        :readonly="isFormLocked"
                    ></kt-input>
                </div>
            </div>
        </div>

        <!-- Status & Scheduling Tab -->
        <div v-show="activeFormTab === 'status'">
            <div class="row">
                <div class="col-6">
                    <kt-select
                        v-model="model.RequestStatus"
                        :options="statusList"
                        label="Status"
                        label-key="label"
                        value-key="value"
                        placeholder="Choose Status"
                    ></kt-select>
                </div>
                <div class="col-6">
                    <kt-select
                        v-model="model.Priority"
                        :options="priorityOptions"
                        label="Priority"
                        label-key="label"
                        value-key="value"
                        placeholder="Choose Priority"
                        :disabled="isFormLocked"
                    ></kt-select>
                </div>
            </div>

            <div class="row mt-2">
                <div class="col-4">
                    <kt-input
                        type="date"
                        label="Due Date"
                        input-class="form-control form-control-sm"
                        v-model="model.DueDate"
                        :disabled="isFormLocked"
                        :readonly="isFormLocked"
                    ></kt-input>
                </div>
                <div class="col-4">
                    <kt-input
                        type="date"
                        label="Completion Date"
                        input-class="form-control form-control-sm"
                        v-model="model.CompletionDate"
                        :disabled="isFormLocked"
                        :readonly="isFormLocked"
                    ></kt-input>
                </div>
                <div class="col-4">
                    <kt-input
                        type="number"
                        label="Progress %"
                        input-class="form-control form-control-sm"
                        v-model="model.Progress"
                        placeholder="0-100"
                        :disabled="isFormLocked"
                        :readonly="isFormLocked"
                    ></kt-input>
                </div>
            </div>
        </div>

        <!-- Project and Billing Info Tab (only visible to users with Billing role) -->
        <!-- Commented out per request - task #8 - - Project Information 
        <div v-if="hasBillingAccess" v-show="activeFormTab === 'billing'">
            <div class="row">
                <div class="col-12">
                    <h6 class="text-muted mb-2">Project Information</h6>
                </div>
            </div>
            <div class="row">
                <div class="col-6">
                    <kt-input
                        type="text"
                        label="Project Folder"
                        input-class="form-control form-control-sm"
                        v-model="model.ProjectFolder"
                        placeholder="Project Folder Path"
                        :disabled="isFormLocked"
                        :readonly="isFormLocked"
                    ></kt-input>
                </div>
                <div class="col-6">
                    <kt-input
                        type="text"
                        label="Work Time"
                        input-class="form-control form-control-sm"
                        v-model="model.WorkTime"
                        placeholder="Work Time"
                        :disabled="isFormLocked"
                        :readonly="isFormLocked"
                    ></kt-input>
                </div>
            </div>

            < ! - - Estimates - - >
            <div class="row mt-3">
                <div class="col-12">
                    <h6 class="text-muted mb-2">Estimates</h6>
                </div>
            </div>
            <div class="row">
                <div class="col-4">
                    <kt-input
                        type="number"
                        label="Estimated Amount"
                        input-class="form-control form-control-sm"
                        v-model="model.EstimatedAmount"
                        placeholder="0.00"
                        step="0.01"
                        :disabled="isFormLocked"
                        :readonly="isFormLocked"
                    ></kt-input>
                </div>
                <div class="col-4">
                    <kt-input
                        type="text"
                        label="Estimate Number"
                        input-class="form-control form-control-sm"
                        v-model="model.EstimateNumber"
                        placeholder="Estimate #"
                        :disabled="isFormLocked"
                        :readonly="isFormLocked"
                    ></kt-input>
                </div>
                <div class="col-4">
                    <kt-input
                        type="text"
                        label="Time Estimate"
                        input-class="form-control form-control-sm"
                        v-model="model.TimeEstimate"
                        placeholder="Time Estimate"
                        :disabled="isFormLocked"
                        :readonly="isFormLocked"
                    ></kt-input>
                </div>
            </div>

            < ! - - Payment Information - - >
            <div class="row mt-3">
                <div class="col-12">
                    <h6 class="text-muted mb-2">Payment Information</h6>
                </div>
            </div>
            <div class="row">
                <div class="col-6">
                    <kt-input
                        type="date"
                        label="Paid On"
                        input-class="form-control form-control-sm"
                        v-model="model.PaidOn"
                        :disabled="isFormLocked"
                        :readonly="isFormLocked"
                    ></kt-input>
                </div>
            </div>

            < ! - - Invoice Information - - >
            <div class="row mt-3">
                <div class="col-12">
                    <h6 class="text-muted mb-2">Invoice Information</h6>
                </div>
            </div>
            <div class="row">
                <div class="col-4">
                    <kt-input
                        type="text"
                        label="Invoice Number"
                        input-class="form-control form-control-sm"
                        v-model="model.InvoiceNumber"
                        placeholder="Invoice #"
                        :disabled="isFormLocked"
                        :readonly="isFormLocked"
                    ></kt-input>
                </div>
                <div class="col-4">
                    <kt-input
                        type="date"
                        label="Invoiced On"
                        input-class="form-control form-control-sm"
                        v-model="model.InvoicedOn"
                        :disabled="isFormLocked"
                        :readonly="isFormLocked"
                    ></kt-input>
                </div>
                <div class="col-4">
                    <div class="d-flex align-items-end gap-1">
                        <kt-input
                            type="number"
                            label="Invoice Total"
                            input-class="form-control form-control-sm"
                            v-model="model.InvoiceTotal"
                            placeholder="0.00"
                            step="0.01"
                            :disabled="isFormLocked"
                            :readonly="isFormLocked"
                            class="flex-grow-1"
                        ></kt-input>
                        <button 
                            type="button" 
                            class="btn btn-sm btn-outline-secondary mb-0" 
                            @click="refreshInvoiceTotal"
                            :disabled="isFormLocked"
                            title="Recalculate Invoice Total from invoices"
                        >
                            <i class="fas fa-sync-alt"></i>
                        </button>
                    </div>
                </div>
            </div>

            <div class="row mt-2">
                <div class="col">
                    <kt-input
                        type="textarea"
                        label="Invoice Description"
                        input-class="form-control form-control-sm"
                        v-model="model.InvoiceDescription"
                        placeholder="Invoice Description"
                        :rows="2"
                        :disabled="isFormLocked"
                        :readonly="isFormLocked"
                    ></kt-input>
                </div>
            </div>

            < ! - - Secondary Invoice - - >
            <div class="row mt-3">
                <div class="col-12">
                    <h6 class="text-muted mb-2">Secondary Invoice</h6>
                </div>
            </div>

            <div class="row">
                <div class="col-4">
                    <kt-input
                        type="text"
                        label="Invoice Number 2"
                        input-class="form-control form-control-sm"
                        v-model="model.InvoiceNumber2"
                        placeholder="Invoice #2"
                        :disabled="isFormLocked"
                        :readonly="isFormLocked"
                    ></kt-input>
                </div>
                <div class="col-4">
                    <kt-input
                        type="date"
                        label="Invoiced On 2"
                        input-class="form-control form-control-sm"
                        v-model="model.InvoicedOn2"
                        :disabled="isFormLocked"
                        :readonly="isFormLocked"
                    ></kt-input>
                </div>
                <div class="col-4">
                    <div class="d-flex align-items-end gap-1">
                        <kt-input
                            type="number"
                            label="Should Bill Total"
                            input-class="form-control form-control-sm"
                            v-model="model.ShouldBillTotal"
                            placeholder="0.00"
                            step="0.01"
                            :disabled="isFormLocked"
                            :readonly="isFormLocked"
                            class="flex-grow-1"
                        ></kt-input>
                        <button 
                            type="button" 
                            class="btn btn-sm btn-outline-secondary mb-0" 
                            @click="refreshShouldBillTotal"
                            :disabled="isFormLocked"
                            title="Recalculate Should Bill Total from time entries"
                        >
                            <i class="fas fa-sync-alt"></i>
                        </button>
                    </div>
                </div>
            </div>

            <div class="row mt-2">
                <div class="col">
                    <kt-input
                        type="textarea"
                        label="Invoice Description 2"
                        input-class="form-control form-control-sm"
                        v-model="model.InvoiceDescription2"
                        placeholder="Invoice Description 2"
                        :rows="2"
                        :disabled="isFormLocked"
                        :readonly="isFormLocked"
                    ></kt-input>
                </div>
            </div>
            -->

        <!-- Access Control Tab (Admin Only) -->
        <div v-if="isSystemAdmin" v-show="activeFormTab === 'access'">
            <div class="row">
                <div class="col-12">
                    <div class="alert alert-info mb-3">
                        <i class="fas fa-info-circle me-2"></i>
                        <strong>Role-Based Access Control:</strong> 
                        Assign roles to restrict who can view this job. 
                        Jobs with no roles assigned are visible to everyone.
                        Users need at least one matching role to access restricted jobs.
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-md-6">
                    <h6 class="text-muted mb-2">Assigned Roles</h6>
                    <div v-if="assignedRoles.length === 0" class="text-muted fst-italic mb-2">
                        No roles assigned - this job is visible to everyone
                    </div>
                    <div v-else class="mb-3">
                        <span 
                            v-for="role in assignedRoles" 
                            :key="role.RoleId" 
                            class="badge bg-primary me-2 mb-2"
                            style="font-size: 0.9em;"
                        >
                            {{ role.RoleName }}
                            <button 
                                type="button" 
                                class="btn-close btn-close-white ms-2" 
                                style="font-size: 0.6em;"
                                @click="removeRole(role.RoleId)"
                                :disabled="isFormLocked"
                            ></button>
                        </span>
                    </div>
                </div>

                <div class="col-md-6">
                    <h6 class="text-muted mb-2">Add Role</h6>
                    <div class="input-group mb-3">
                        <select 
                            class="form-select form-select-sm" 
                            v-model="selectedRoleToAdd"
                            :disabled="isFormLocked || availableRolesToAdd.length === 0"
                        >
                            <option value="">Select a role to add...</option>
                            <option 
                                v-for="role in availableRolesToAdd" 
                                :key="role.Id" 
                                :value="role.Id"
                            >
                                {{ role.Name }}
                            </option>
                        </select>
                        <button 
                            class="btn btn-sm btn-outline-primary" 
                            type="button"
                            @click="addRole"
                            :disabled="!selectedRoleToAdd || isFormLocked"
                        >
                            <i class="fas fa-plus"></i> Add
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Messages Tab Content -->
        <div v-show="activeFormTab === 'messages'" v-if="model.Id">
            <job-messages-tab :request-id="model.Id" />
        </div>

        <!-- Notes Tab Content (Audit Trail + Manual Notes) -->
        <div v-show="activeFormTab === 'notes'" v-if="model.Id">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0">
                    <FileText :size="18" class="me-2" style="vertical-align: text-bottom;" />
                    Job Activity Log
                </h6>
                <button 
                    class="btn btn-sm btn-outline-primary"
                    @click="showAddNoteModal = true"
                    :disabled="isFormLocked"
                >
                    <i class="fas fa-plus me-1"></i> Add Note
                </button>
            </div>
            
            <!-- Notes List -->
            <div v-if="jobNotes.length === 0" class="alert alert-secondary">
                <i class="fas fa-info-circle me-2"></i>
                No notes or activity recorded yet.
            </div>
            <div v-else class="notes-list">
                <div 
                    v-for="note in jobNotes" 
                    :key="note.Id" 
                    class="card mb-2"
                    :class="{ 'border-primary': note.IsSystemGenerated }"
                >
                    <div class="card-body py-2 px-3">
                        <div class="d-flex justify-content-between align-items-start">
                            <div>
                                <small class="text-muted">
                                    {{ formatNoteDate(note.DateTaken) }} - 
                                    <strong>{{ note.AuthorName || 'System' }}</strong>
                                    <span v-if="note.IsSystemGenerated" class="badge bg-secondary ms-2">Audit</span>
                                </small>
                            </div>
                            <button 
                                v-if="!note.IsSystemGenerated"
                                class="btn btn-sm btn-link text-danger p-0"
                                @click="deleteNote(note.Id)"
                                :disabled="isFormLocked"
                            >
                                <i class="fas fa-times"></i>
                            </button>
                        </div>
                        <p class="mb-0 mt-1">{{ note.Note }}</p>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- References Section (moved to bottom of modal body) -->
    <div v-if="model.Id" class="mt-3">
        <ul class="nav nav-tabs" role="tablist">
            <li class="nav-item" role="presentation">
                <button 
                    class="nav-link" 
                    :class="{ active: activeTab === 'assignments' }"
                    @click="activeTab = 'assignments'"
                    type="button"
                >
                    <span class="badge" :class="activeTab === 'assignments' ? 'tab-badge-active' : 'tab-badge'">
                        <ClipboardCheck :size="16" class="me-1" style="vertical-align: text-bottom;" />
                        Tasks <span class="ms-1">({{ tabCounts.tasks }})</span>
                    </span>
                </button>
            </li>
            <li class="nav-item" role="presentation">
                <button 
                    class="nav-link" 
                    :class="{ active: activeTab === 'work' }"
                    @click="activeTab = 'work'"
                    type="button"
                >
                    <span class="badge" :class="activeTab === 'work' ? 'tab-badge-active' : 'tab-badge'">
                        <Clock :size="16" class="me-1" style="vertical-align: text-bottom;" />
                        Work <span class="ms-1">({{ tabCounts.work }})</span>
                    </span>
                </button>
            </li>
            <li class="nav-item" role="presentation">
                <button 
                    class="nav-link" 
                    :class="{ active: activeTab === 'parts' }"
                    @click="activeTab = 'parts'"
                    type="button"
                >
                    <span class="badge" :class="activeTab === 'parts' ? 'tab-badge-active' : 'tab-badge'">
                        <Package :size="16" class="me-1" style="vertical-align: text-bottom;" />
                        Parts <span class="ms-1">({{ tabCounts.parts }})</span>
                    </span>
                </button>
            </li>
            <li class="nav-item" role="presentation">
                <button 
                    class="nav-link" 
                    :class="{ active: activeTab === 'expenses' }"
                    @click="activeTab = 'expenses'"
                    type="button"
                >
                    <span class="badge" :class="activeTab === 'expenses' ? 'tab-badge-active' : 'tab-badge'">
                        <Banknote :size="16" class="me-1" style="vertical-align: text-bottom;" />
                        Expenses <span class="ms-1">({{ tabCounts.expenses }})</span>
                    </span>
                </button>
            </li>
            <li class="nav-item" role="presentation">
                <button 
                    class="nav-link" 
                    :class="{ active: activeTab === 'mileage' }"
                    @click="activeTab = 'mileage'"
                    type="button"
                >
                    <span class="badge" :class="activeTab === 'mileage' ? 'tab-badge-active' : 'tab-badge'">
                        <Car :size="16" class="me-1" style="vertical-align: text-bottom;" />
                        Mileage <span class="ms-1">({{ tabCounts.mileage }})</span>
                    </span>
                </button>
            </li>
            <li class="nav-item" role="presentation">
                <button 
                    class="nav-link" 
                    :class="{ active: activeTab === 'assets' }"
                    @click="activeTab = 'assets'"
                    type="button"
                >
                    <span class="badge" :class="activeTab === 'assets' ? 'tab-badge-active' : 'tab-badge'">
                        <Paperclip :size="16" class="me-1" style="vertical-align: text-bottom;" />
                        Attachments <span class="ms-1">({{ tabCounts.attachments }})</span>
                    </span>
                </button>
            </li>
            <li class="nav-item ms-auto" role="presentation">
                <button 
                    class="nav-link" 
                    :class="{ active: activeTab === 'requests' }"
                    @click="activeTab = 'requests'"
                    type="button"
                >
                    <span class="badge" :class="activeTab === 'requests' ? 'tab-badge-active' : 'tab-badge'">
                        <ClipboardList :size="16" class="me-1" style="vertical-align: text-bottom;" />
                        Requests <span class="ms-1">({{ tabCounts.requests }})</span>
                    </span>
                </button>
            </li>
            <li class="nav-item" role="presentation">
                <button 
                    class="nav-link" 
                    :class="{ active: activeTab === 'estimates' }"
                    @click="activeTab = 'estimates'"
                    type="button"
                >
                    <span class="badge" :class="activeTab === 'estimates' ? 'tab-badge-active' : 'tab-badge'">
                        <FileText :size="16" class="me-1" style="vertical-align: text-bottom;" />
                        Estimates <span class="ms-1">({{ tabCounts.estimates }})</span>
                    </span>
                </button>
            </li>
            <li class="nav-item" role="presentation">
                <button 
                    class="nav-link" 
                    :class="{ active: activeTab === 'invoices' }"
                    @click="activeTab = 'invoices'"
                    type="button"
                >
                    <span class="badge" :class="activeTab === 'invoices' ? 'tab-badge-active' : 'tab-badge'">
                        <FileStack :size="16" class="me-1" style="vertical-align: text-bottom;" />
                        Invoices <span class="ms-1">({{ tabCounts.invoices }})</span>
                    </span>
                </button>
            </li>
        </ul>

        <div class="tab-content border border-top-0 p-3">
            <!-- Tasks Tab (renamed from Assignments) -->
            <div v-show="activeTab === 'assignments'">
                <h6 class="mb-3">
                    <ClipboardCheck :size="18" class="me-2" style="vertical-align: text-bottom;" />
                    Tasks
                </h6>
                <assignment-datatable 
                    :request-id="model.Id"
                    :client-id="model.Customer"
                    :tech-id="model.Technician"
                    :hide-filters="true"
                    :embedded="true"
                    :disable-actions="isFormLocked"
                    @count-loaded="(count) => tabCounts.tasks = count"
                ></assignment-datatable>
            </div>
            
            <!-- Work Tab -->
            <div v-show="activeTab === 'work'">
                <h6 class="mb-3">
                    <Clock :size="18" class="me-2" style="vertical-align: text-bottom;" />
                    Work
                </h6>
                <work-datatable 
                    :request-id="model.Id"
                    :client-id="model.Customer"
                    :tech-id="model.Technician"
                    :hide-filters="true"
                    :embedded="true"
                    :disable-actions="isFormLocked"
                    @count-loaded="(count) => tabCounts.work = count"
                ></work-datatable>
            </div>

            <!-- Parts Tab -->
            <div v-show="activeTab === 'parts'">
                <h6 class="mb-3">
                    <Package :size="18" class="me-2" style="vertical-align: text-bottom;" />
                    Parts
                </h6>
                <part-list 
                    :request-id="model.Id"
                    :client-id="model.Customer"
                    :tech-id="model.Technician"
                    :hide-filters="true"
                    :embedded="true"
                    :disable-actions="isFormLocked"
                    @count-loaded="(count) => tabCounts.parts = count"
                ></part-list>
            </div>

            <!-- Expenses Tab -->
            <div v-show="activeTab === 'expenses'">
                <h6 class="mb-3">
                    <Banknote :size="18" class="me-2" style="vertical-align: text-bottom;" />
                    Expenses
                </h6>
                <expense-list 
                    :request-id="model.Id"
                    :client-id="model.Customer"
                    :tech-id="model.Technician"
                    :hide-filters="true"
                    :embedded="true"
                    :disable-actions="isFormLocked"
                    @count-loaded="(count) => tabCounts.expenses = count"
                ></expense-list>
            </div>

            <!-- Mileage Tab -->
            <div v-show="activeTab === 'mileage'">
                <h6 class="mb-3">
                    <Car :size="18" class="me-2" style="vertical-align: text-bottom;" />
                    Mileage
                </h6>
                <mileage-list 
                    :request-id="model.Id"
                    :client-id="model.Customer"
                    :tech-id="model.Technician"
                    :hide-filters="true"
                    :embedded="true"
                    :disable-actions="isFormLocked"
                    @count-loaded="(count) => tabCounts.mileage = count"
                ></mileage-list>
            </div>

            <!-- Attachments Tab -->
            <div v-show="activeTab === 'assets'">
                <h6 class="mb-3">
                    <Paperclip :size="18" class="me-2" style="vertical-align: text-bottom;" />
                    Attachments
                </h6>
                <attachment-form
                    :request="model.Id"
                    :embedded="true"
                    :disable-actions="isFormLocked"
                    @count-loaded="(count) => tabCounts.attachments = count"
                />
            </div>

            <!-- Requests Tab -->
            <div v-show="activeTab === 'requests'">
                <h6 class="mb-3">
                    <ClipboardList :size="18" class="me-2" style="vertical-align: text-bottom;" />
                    Requests
                </h6>
                <request-datatable
                    :request-id="model.Id"
                    :client-id="model.Customer"
                    :hide-filters="true"
                    :embedded="true"
                    :disable-actions="isFormLocked"
                    @count-loaded="(count) => tabCounts.requests = count"
                ></request-datatable>
            </div>

            <!-- Estimates Tab -->
            <div v-show="activeTab === 'estimates'">
                <h6 class="mb-3">
                    <FileText :size="18" class="me-2" style="vertical-align: text-bottom;" />
                    Estimates
                </h6>
                <estimate-datatable
                    :request-id="model.Id"
                    :client-id="model.Customer"
                    :hide-filters="true"
                    :embedded="true"
                    :disable-actions="isFormLocked"
                    @count-loaded="(count) => tabCounts.estimates = count"
                ></estimate-datatable>
            </div>

            <!-- Invoices Tab -->
            <div v-show="activeTab === 'invoices'">
                <h6 class="mb-3">
                    <FileStack :size="18" class="me-2" style="vertical-align: text-bottom;" />
                    Invoices
                </h6>
                <invoice-datatable
                    :request-id="model.Id"
                    :client-id="model.Customer"
                    :hide-filters="true"
                    :embedded="true"
                    :disable-actions="isFormLocked"
                    @count-loaded="(count) => tabCounts.invoices = count"
                ></invoice-datatable>
            </div>
        </div>
    </div>

    <!-- Add Note Modal -->
    <kt-modal
        :visible="showAddNoteModal"
        title="Add Note"
        size="modal-md"
        @dismiss="showAddNoteModal = false"
    >
        <template #body>
            <div class="mb-3">
                <label class="form-label">Note</label>
                <textarea 
                    class="form-control" 
                    v-model="newNoteText" 
                    rows="4" 
                    placeholder="Enter your note..."
                ></textarea>
            </div>
        </template>
        <template #footer>
            <div class="kt-modal-actions">
                <button @click="showAddNoteModal = false" class="btn btn-outline-secondary">
                    Cancel
                </button>
                <button @click="saveNote" class="btn btn-primary" :disabled="!newNoteText.trim()">
                    <i class="fas fa-save me-1"></i> Save Note
                </button>
            </div>
        </template>
    </kt-modal>

    <!-- Request Type Selector Modal -->
    <request-type-selector-modal
        v-if="showRequestTypeSelector"
        :visible="showRequestTypeSelector"
        :current-value="model.RequestType"
        :can-manage="isAdmin"
        @close="showRequestTypeSelector = false"
        @select="onRequestTypeSelected"
        @request-types-changed="loadRequestTypeOptions"
    />

    <!-- Invoice Edit Modal (embedded WorkOrderEdit) -->
    <work-order-edit-embedded
        v-if="showInvoiceModal && editingInvoiceId"
        :record-id="editingInvoiceId"
        :request-id="model.Id"
        :client-id="model.Customer"
        @close="handleInvoiceModalClose"
    />

    <!-- Department Selector Modal -->
    <department-selector-modal
        v-if="showDepartmentSelector"
        :visible="showDepartmentSelector"
        :current-value="model.Department"
        :can-manage="isAdmin"
        @close="showDepartmentSelector = false"
        @select="onDepartmentSelected"
        @departments-changed="loadDepartmentOptions"
    />

</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, computed } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import Api from "@/services/Api";
import ApiService from "@/services/ApiService";
import Alert from "@/services/Alert";
import Toast from "@/services/Toast";
import Helper from "@/services/Helper";
import { useJobStatusStore } from "@/stores/JobStatusStore";
import { usePermissions } from "@/composables/usePermissions";

import OptionsService from "@/services/OptionsService";
import { useTechStore } from "@/stores/TechStore";
import { usePriorityStore } from "@/stores/PriorityStore";
import ClientAc from "@/components/Clients/ClientAc.vue";
import TechSelect from "@/components/Techs/TechSelect.vue";
import TechAc from "@/components/Techs/TechAc.vue";
import KtInput from "@/components/UI/KtInput.vue";
import KtSelect from "@/components/UI/KtSelect.vue";
import KtModal from "@/components/UI/KtModal.vue";
import AssignmentList from "@/components/Assignments/AssignmentList.vue";
import AssignmentDatatable from "@/components/Assignments/AssignmentDatatable.vue";
import WorkList from "@/components/Work/WorkList.vue";
import WorkDatatable from "@/components/Work/WorkDatatable.vue";
import PartList from "@/components/Parts/PartList.vue";
import ExpenseList from "@/components/Expenses/ExpenseList.vue";
import MileageList from "@/components/Mileage/MileageList.vue";
import RequestDatatable from "@/components/Requests/RequestsDatatable.vue";
import EstimateDatatable from "@/components/Estimates/EstimateDatatable.vue";
import InvoiceDatatable from "@/components/WorkOrders/WorkOrderDatatable.vue";
import AttachmentForm from "@/components/Attachments/AttachmentForm.vue";
import { ClipboardList, ClipboardCheck, Clock, CalendarClock, DollarSign, FileText, Package, Banknote, Car, Paperclip, FileStack, ShieldCheck, Users, PlayCircle, Trash2, RefreshCw, Search, ExternalLink, Link2, MessageSquare, LockOpen } from 'lucide-vue-next';
import Emitter from "@/services/Emitter";
import RequestTypeSelectorModal from "@/components/Jobs/RequestTypeSelectorModal.vue";
import DepartmentSelectorModal from "@/components/Requests/DepartmentSelectorModal.vue";
import WorkOrderEditEmbedded from "@/components/WorkOrders/WorkOrderEditEmbedded.vue";
import JobMessagesTab from "@/components/Notifications/JobMessagesTab.vue";


// Use Pinia stores instead of static Attic.js
const priorityStore = usePriorityStore();
const { list: priorityList } = storeToRefs(priorityStore);

// Job Status Store (imported and defined later in file around line 1513)
const jobStatusStore = useJobStatusStore();
const { list: jobStatusList } = storeToRefs(jobStatusStore);

// Convert priority store to list format for kt-select
const priorityOptions = computed(() => 
    Object.values(priorityList.value).map(priority => ({
        value: priority.Id,
        label: priority.Level
    }))
);

// Convert job status store to list format for kt-select
const statusList = computed(() => Object.values(jobStatusList.value).map(status => ({
    value: status.Id,
    label: status.Status
})));

// Open project folder URL in new browser tab
const openProjectFolder = () => {
    if (model.value.ProjectFolder) {
        let url = model.value.ProjectFolder;
        // Add protocol if missing
        if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('file://')) {
            url = 'file:///' + url.replace(/\\/g, '/');
        }
        window.open(url, '_blank');
    }
};

// Tech store for assigned technicians lookup
const techStore = useTechStore();
const { list: TechList } = storeToRefs(techStore);

// Check if user has billing access and admin status
const { hasRole, isAdmin } = usePermissions();
const hasBillingAccess = computed(() => hasRole('Billing'));
const isSystemAdmin = isAdmin; // isAdmin is already a computed ref

// Role-based access control state (admin only)
const allRoles = ref([]);
const assignedRoles = ref([]);
const selectedRoleToAdd = ref('');
const pendingRoleIds = ref([]); // Track roles for new jobs before save

// Staff assignment state (legacy - keeping for backward compatibility)
const assignedStaff = ref([]);
const allStaff = ref([]);
const selectedStaffToAdd = ref('');

// Assigned Technicians state (using Options table)
const assignedTechnicians = ref([]);
const selectedTechToAdd = ref(null);
const ASSIGNED_TECHS_OPTION_KEY = 'request_assigned_techs_';

// Department/Category state (using Options table)
const departmentOptions = ref([]);
const showDepartmentSelector = ref(false);
const DEPARTMENT_OPTION_KEY = 'departments_json';

// Request Type state (using Options table)
const requestTypeOptions = ref([]);
const showRequestTypeSelector = ref(false);
const REQUEST_TYPES_OPTION_KEY = 'request_types_json';

// Invoice modal state (embedded WorkOrderEdit)
const showInvoiceModal = ref(false);
const editingInvoiceId = ref(null);

// Job Notes state (for Notes tab - audit trail + manual notes)
const jobNotes = ref([]);
const showAddNoteModal = ref(false);
const newNoteText = ref('');

// Load job notes
const loadJobNotes = async () => {
    if (!model.value?.Id) {
        jobNotes.value = [];
        return;
    }
    try {
        // Load notes from the request's notes/history
        const notes = await ApiService.loadRequestNotes(model.value.Id, true);
        jobNotes.value = notes || [];
    } catch (error) {
        console.error('Failed to load job notes:', error);
        jobNotes.value = [];
    }
};

// Format note date for display
const formatNoteDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
};

// Save a new note
const saveNote = async () => {
    if (!newNoteText.value.trim() || !model.value?.Id) return;
    
    try {
        await ApiService.saveRequestNote({
            RequestId: model.value.Id,
            Note: newNoteText.value.trim(),
            IsSystemGenerated: false
        });
        
        newNoteText.value = '';
        showAddNoteModal.value = false;
        await loadJobNotes();
        Alert.success('Note added successfully');
    } catch (error) {
        console.error('Failed to save note:', error);
        Alert.error('Failed to save note');
    }
};

// Delete a note
const deleteNote = async (noteId) => {
    if (!noteId) return;
    
    Alert.confirm('Are you sure you want to delete this note?', async () => {
        try {
            await ApiService.deleteRequestNote(noteId);
            await loadJobNotes();
            Alert.success('Note deleted successfully');
        } catch (error) {
            console.error('Failed to delete note:', error);
            Alert.error('Failed to delete note');
        }
    });
};

const openNewInvoice = () => {
    editingInvoiceId.value = 'new';
    showInvoiceModal.value = true;
};

const handleInvoiceModalClose = () => {
    showInvoiceModal.value = false;
    editingInvoiceId.value = null;
    // Refresh the invoice datatable
    Emitter.emit('datatable-refresh', 'workorder-embedded');
};

// Load all staff for dropdown
const loadAllStaff = async () => {
    try {
        const response = await ApiService.loadStaff({ Active: 1 }, true);
        allStaff.value = response?.Data ?? [];
    } catch (error) {
        console.error('Failed to load staff:', error);
    }
};

// Load staff assigned to this request
const loadAssignedStaff = async () => {
    if (!model.value?.Id) return;
    try {
        assignedStaff.value = await ApiService.getRequestStaff(model.value.Id, true);
    } catch (error) {
        console.error('Failed to load assigned staff:', error);
        assignedStaff.value = [];
    }
};

// Computed: staff available to add (not already assigned)
const availableStaffToAdd = computed(() => {
    const assignedIds = assignedStaff.value.map(s => s.StaffId);
    return allStaff.value.filter(s => !assignedIds.includes(s.Id));
});

// Add staff to this request
const addStaff = async () => {
    if (!selectedStaffToAdd.value || !model.value?.Id) return;
    
    try {
        await ApiService.assignStaffToRequest(model.value.Id, selectedStaffToAdd.value, 0, true);
        await loadAssignedStaff();
        selectedStaffToAdd.value = '';
        Alert.success('Staff assigned successfully');
    } catch (error) {
        console.error('Failed to assign staff:', error);
        Alert.error('Failed to assign staff');
    }
};

// Remove staff from this request
const removeStaff = async (mappingId) => {
    try {
        await ApiService.removeStaffFromRequest(mappingId, true);
        await loadAssignedStaff();
        Alert.success('Staff removed successfully');
    } catch (error) {
        console.error('Failed to remove staff:', error);
        Alert.error('Failed to remove staff');
    }
};

// ===== ASSIGNED TECHNICIANS (using Options table) =====

// Load assigned technicians for this request from Options table
const loadAssignedTechnicians = async () => {
    if (!model.value?.Id) {
        assignedTechnicians.value = [];
        return;
    }
    try {
        const optionValue = await OptionsService.get(ASSIGNED_TECHS_OPTION_KEY + model.value.Id);
        if (optionValue) {
            assignedTechnicians.value = JSON.parse(optionValue);
        } else {
            assignedTechnicians.value = [];
        }
    } catch (error) {
        console.error('Failed to load assigned technicians:', error);
        assignedTechnicians.value = [];
    }
};

// Save assigned technicians to Options table
const saveAssignedTechnicians = async () => {
    if (!model.value?.Id) return;
    try {
        await OptionsService.set(
            ASSIGNED_TECHS_OPTION_KEY + model.value.Id, 
            JSON.stringify(assignedTechnicians.value)
        );
    } catch (error) {
        console.error('Failed to save assigned technicians:', error);
    }
};

// Add a technician to the assigned list
const addAssignedTechnician = async () => {
    if (!selectedTechToAdd.value) return;
    
    // Get technician name from TechStore
    const tech = TechList.value[selectedTechToAdd.value];
    if (!tech) return;
    
    // Check if already assigned
    if (assignedTechnicians.value.some(t => t.id === selectedTechToAdd.value)) {
        Alert.notify({ text: 'Technician already assigned', type: 'warning' });
        selectedTechToAdd.value = null;
        return;
    }
    
    assignedTechnicians.value.push({
        id: selectedTechToAdd.value,
        name: tech.TechnicianName || tech.Name || 'Unknown'
    });
    
    await saveAssignedTechnicians();
    selectedTechToAdd.value = null;
};

// Remove a technician from the assigned list
const removeAssignedTechnician = async (techId) => {
    assignedTechnicians.value = assignedTechnicians.value.filter(t => t.id !== techId);
    await saveAssignedTechnicians();
};

// ===== REQUEST TYPES (using Options table) =====

// Load request type options from Options table
const loadRequestTypeOptions = async () => {
    try {
        const optionValue = await OptionsService.get(REQUEST_TYPES_OPTION_KEY);
        if (optionValue) {
            const types = JSON.parse(optionValue);
            requestTypeOptions.value = types.map(t => ({ value: t.name, label: t.name }));
        } else {
            requestTypeOptions.value = [];
        }
    } catch (error) {
        console.error('Failed to load request types:', error);
        requestTypeOptions.value = [];
    }
};

// Handle request type selection from modal
const onRequestTypeSelected = (type) => {
    model.value.RequestType = type.name;
    showRequestTypeSelector.value = false;
};

// ===== DEPARTMENTS (using Options table) =====

// Load department options from Options table
const loadDepartmentOptions = async () => {
    try {
        const optionValue = await OptionsService.get(DEPARTMENT_OPTION_KEY);
        if (optionValue) {
            const depts = JSON.parse(optionValue);
            departmentOptions.value = depts.map(d => ({ value: d.name, label: d.name }));
        } else {
            departmentOptions.value = [];
        }
    } catch (error) {
        console.error('Failed to load departments:', error);
        departmentOptions.value = [];
    }
};

// Open department selector modal
const openDepartmentSelector = () => {
    showDepartmentSelector.value = true;
};

// Handle department selection from modal
const onDepartmentSelected = (dept) => {
    model.value.Department = dept.name;
    showDepartmentSelector.value = false;
};

// Computed: roles available to add (not already assigned)
const availableRolesToAdd = computed(() => {
    const assignedIds = assignedRoles.value.map(r => r.RoleId);
    return allRoles.value.filter(r => !assignedIds.includes(r.Id));
});

// Load all available roles (for admin dropdown)
const loadAllRoles = async () => {
    if (!isSystemAdmin.value) return;
    try {
        allRoles.value = await ApiService.loadRoles(true);
    } catch (error) {
        console.error('Failed to load roles:', error);
    }
};

// Load roles assigned to this request
const loadAssignedRoles = async () => {
    if (!isSystemAdmin.value || !model.value?.Id) return;
    try {
        assignedRoles.value = await ApiService.getRequestRoles(model.value.Id, true);
    } catch (error) {
        console.error('Failed to load assigned roles:', error);
        assignedRoles.value = [];
    }
};

// Add a role to this request
const addRole = async () => {
    if (!selectedRoleToAdd.value) return;
    
    // For new jobs (no Id), track roles locally
    if (!model.value?.Id) {
        const roleToAdd = allRoles.value.find(r => r.Id === selectedRoleToAdd.value);
        if (roleToAdd && !pendingRoleIds.value.includes(selectedRoleToAdd.value)) {
            pendingRoleIds.value.push(selectedRoleToAdd.value);
            assignedRoles.value.push({
                RoleId: roleToAdd.Id,
                RoleName: roleToAdd.Name
            });
        }
        selectedRoleToAdd.value = '';
        return;
    }
    
    // For existing jobs, save to server immediately
    try {
        await ApiService.addRequestRole(model.value.Id, selectedRoleToAdd.value, true);
        await loadAssignedRoles();
        selectedRoleToAdd.value = '';
        Alert.success('Role added successfully');
    } catch (error) {
        console.error('Failed to add role:', error);
        Alert.error('Failed to add role');
    }
};

// Remove a role from this request
const removeRole = async (roleId) => {
    // For new jobs (no Id), remove from local tracking
    if (!model.value?.Id) {
        pendingRoleIds.value = pendingRoleIds.value.filter(id => id !== roleId);
        assignedRoles.value = assignedRoles.value.filter(r => r.RoleId !== roleId);
        return;
    }
    
    // For existing jobs, remove from server
    try {
        await ApiService.removeRequestRole(model.value.Id, roleId, true);
        await loadAssignedRoles();
        Alert.success('Role removed successfully');
    } catch (error) {
        console.error('Failed to remove role:', error);
        Alert.error('Failed to remove role');
    }
};

// Get pending role IDs (for new jobs that haven't been saved yet)
const getPendingRoleIds = () => {
    return pendingRoleIds.value;
};

// Clear pending roles (called after successful save)
const clearPendingRoles = () => {
    pendingRoleIds.value = [];
};

// Expose methods for parent component
defineExpose({
    getPendingRoleIds,
    clearPendingRoles
});

const _ = ref({});

// Admin unlock override (temporary, resets on page reload)
const adminUnlocked = ref(false);

// Closed statuses that lock the form
const closedStatuses = ref([]);

// Load closed statuses
const loadClosedStatuses = async () => {
    try {
        closedStatuses.value = await ApiService.loadClosedStatuses(true);
    } catch (error) {
        console.error('Failed to load closed statuses:', error);
    }
};

// Computed property to check if form should be locked
const isFormLocked = computed(() => {
    if (adminUnlocked.value) return false; // Admin override
    
    if (!model.value?.Id || !model.value?.RequestStatus) {
        return false; // New job or no status, not locked
    }
    
    if (!jobStatusList.value || Object.keys(jobStatusList.value).length === 0) {
        return false; // Store not loaded yet
    }
    
    const currentStatus = jobStatusList.value[model.value.RequestStatus];
    if (!currentStatus || !currentStatus.Status) {
        return false;
    }
    
    return closedStatuses.value.some(closedStatus => 
        closedStatus.toLowerCase() === currentStatus.Status.toLowerCase()
    );
});

// Get current status name for display
const currentStatusName = computed(() => {
    if (!model.value?.RequestStatus) return '';
    if (!jobStatusList.value) return '';
    const status = jobStatusList.value[model.value.RequestStatus];
    return status?.Status || '';
});

// Active tab state for main form tabs
const activeFormTab = ref('workorder');

// Active tab state for bottom tabs
const activeTab = ref('assignments');

// Tab counts for badges
const tabCounts = ref({
    tasks: 0,
    work: 0,
    parts: 0,
    expenses: 0,
    mileage: 0,
    attachments: 0,
    requests: 0,
    estimates: 0,
    invoices: 0
});

const unwatch = [];

const route = useRoute();
const model = defineModel({
    type: Object,
    default: { }
});

const load = async () =>
{
    // Reset counts for new jobs
    if (!model.value?.Id) {
        Object.keys(tabCounts.value).forEach(key => tabCounts.value[key] = 0);
    }
    // Counts for tasks, work, parts, expenses, mileage, attachments are now received via @count-loaded events
    // TODO: Add count emission for WorkOrder lists (Requests, Estimates, Invoices) when those components are updated
};

// ===== TOOLBAR ACTIONS =====

// Start Clock - emit event to open global clock form
const startClock = () => {
    if (!model.value?.Id) return;
    Emitter.emit('new:clock', { requestId: model.value.Id });
};

// Add Assignment - emit event to open global assignment form
const addAssignment = () => {
    if (!model.value?.Id) return;
    Emitter.emit('new:assignment', { 
        requestId: model.value.Id,
        clientId: model.value.Customer
    });
};

// Add Work - emit event to open global work form
const addWork = () => {
    if (!model.value?.Id) return;
    Emitter.emit('open:work', { 
        id: null, // New work entry
        requestId: model.value.Id,
        clientId: model.value.Customer,
        techId: model.value.Technician
    });
};

// Add Estimate - emit event to open global estimate form
const addEstimate = () => {
    if (!model.value?.Id) return;
    Emitter.emit('open:estimateForm', { 
        requestId: model.value.Id,
        clientId: model.value.Customer,
        techId: model.value.Technician
    });
};

// Add Part - emit event to open global part form
const addPart = () => {
    if (!model.value?.Id) return;
    Emitter.emit('open:partForm', { 
        requestId: model.value.Id,
        clientId: model.value.Customer,
        techId: model.value.Technician
    });
};

// Add Expense - emit event to open global expense form
const addExpense = () => {
    if (!model.value?.Id) return;
    Emitter.emit('open:expenseForm', { 
        requestId: model.value.Id,
        clientId: model.value.Customer,
        techId: model.value.Technician
    });
};

// Add Mileage - emit event to open global mileage form
const addMileage = () => {
    if (!model.value?.Id) return;
    Emitter.emit('open:mileageForm', { 
        requestId: model.value.Id,
        clientId: model.value.Customer,
        techId: model.value.Technician
    });
};

// Refresh Job - reload all job data from the server
const refreshJob = async () => {
    if (!model.value?.Id) return;
    try {
        const data = await ApiService.loadRequest(model.value.Id + '');
        Object.assign(model.value, data);
        await loadJobNotes();
        // AttachmentForm handles its own file loading
        Emitter.emit('datatable-refresh', 'workorder-embedded');
        Toast.success('Job refreshed');
    } catch (error) {
        console.error('Error refreshing job:', error);
    }
};

// Delete Job - confirm and delete
const deleteJob = () => {
    if (!model.value?.Id) return;
    Alert.confirm("Are you sure you want to delete this job?", async () => {
        try {
            await ApiService.deleteWorkOrder(model.value.Id);
            Alert.notify({ text: "Job deleted successfully", type: "success" });
            Emitter.emit('redirect', '/job/list');
        } catch (error) {
            console.error('Error deleting job:', error);
            Alert.notify({ text: "Failed to delete job", type: "error" });
        }
    });
};

/**
 * Refresh Invoice Total by summing all invoices for this job
 */
const refreshInvoiceTotal = async () => {
    if (!model.value?.Id) return;
    
    try {
        // Load all invoices for this job
        const response = await ApiService.loadWorkOrders({
            RequestId: [model.value.Id],
            Title: 'Invoice'
        });
        
        const invoices = response?.Data ?? [];
        let total = 0;
        
        invoices.forEach((invoice) => {
            if (invoice.Total) {
                total += parseFloat(invoice.Total) || 0;
            }
        });
        
        model.value.InvoiceTotal = total;
        Toast.success(`Invoice Total updated: $${total.toFixed(2)}`);
    } catch (error) {
        console.error('Error refreshing invoice total:', error);
        Alert.notify('Failed to refresh Invoice Total');
    }
};

/**
 * Refresh Should Bill Total by summing Work (ShouldBill), Parts, Expenses, and Mileage
 */
const refreshShouldBillTotal = async () => {
    if (!model.value?.Id) return;
    
    try {
        let total = 0;
        
        // 1. Sum Work entries (ShouldBill field)
        const workResponse = await ApiService.loadTimes({
            RequestId: [model.value.Id],
            TimeType: 'Work'
        });
        const workEntries = workResponse?.Data ?? [];
        workEntries.forEach((entry) => {
            total += parseFloat(entry.ShouldBill) || 0;
        });
        
        // 2. Sum Parts (PartsCost or MSRP)
        const partsResponse = await ApiService.loadParts({
            RequestId: [model.value.Id]
        });
        const parts = partsResponse?.Data ?? [];
        parts.forEach((part) => {
            total += parseFloat(part.PartsCost) || parseFloat(part.MSRP) || 0;
        });
        
        // 3. Sum Expenses
        const expensesResponse = await ApiService.loadExpenses({
            RequestId: [model.value.Id]
        });
        const expenses = expensesResponse?.Data ?? [];
        expenses.forEach((expense) => {
            total += parseFloat(expense.ExpenseAmount) || 0;
        });
        
        // 4. Sum Mileage (Miles * rate - using standard rate if not specified)
        const mileageResponse = await ApiService.loadMileage({
            RequestId: [model.value.Id]
        });
        const mileageEntries = mileageResponse?.Data ?? [];
        mileageEntries.forEach((mileage) => {
            // Use MileageAmount if available, otherwise calculate from miles
            total += parseFloat(mileage.MileageAmount) || 0;
        });
        
        model.value.ShouldBillTotal = total;
        Toast.success(`Should Bill Total updated: $${total.toFixed(2)}`);
    } catch (error) {
        console.error('Error refreshing should bill total:', error);
        Alert.notify({ text: 'Failed to refresh Should Bill Total', type: 'error' });
    }
};

onMounted(async () =>
{
    // Load closed statuses
    await loadClosedStatuses();
    
    // Ensure job status store is loaded
    await jobStatusStore.reload();

    // Load roles for admin access control
    if (isSystemAdmin.value) {
        await loadAllRoles();
    }
    
    // Load all staff for assignment dropdown
    await loadAllStaff();
    
    // Load request type options (global, not per-request)
    await loadRequestTypeOptions();
    
    // Load department options (global, not per-request)
    await loadDepartmentOptions();
    
    unwatch.push(
        watch(() =>
        {
            return model.value?.Id;
        }, (nv, ov) => {
            load();
            // Load assigned roles when model ID changes (for admins)
            if (isSystemAdmin.value && nv) {
                loadAssignedRoles();
            }
            // Load assigned staff when model ID changes
            if (nv) {
                loadAssignedStaff();
            }
            // Load assigned technicians when model ID changes
            if (nv) {
                loadAssignedTechnicians();
            }
            // Load job notes when model ID changes
            if (nv) {
                loadJobNotes();
            }
        }, { immediate: true })
    );
});

onUnmounted(() =>
{
    unwatch.forEach((un) => {
        un();
    });
});

</script>

<style scoped>
/* Tab content styling */
.tab-content {
    min-height: 300px;
}

/* Tab badge styling */
.tab-badge {
    background-color: #e9ecef;
    color: #495057;
    font-weight: 500;
    padding: 0.35em 0.65em;
}

.tab-badge-active {
    background-color: #3B9796;
    color: #fff;
    font-weight: 500;
    padding: 0.35em 0.65em;
}

.nav-link {
    border: none;
    background: transparent;
}

.nav-link:hover .tab-badge {
    background-color: #3B9796;
    color: #fff;
}
</style>
